import { Icon } from '~/design-system/elements/Icon/Icon';
import styles from './AttachmentCardUI.module.css';

export type AttachmentCardKind = 'image' | 'file';

export interface AttachmentCardUIProps {
  kind: AttachmentCardKind;
  /** image: preview source + alt */
  src?: string;
  alt?: string;
  /** file: short type label ("PDF", "MD", …) + display name */
  fileType?: string;
  name?: string;
  onClick?: () => void;
  /** When provided (e.g. in the input bar), a remove control shows on the card. */
  onRemove?: () => void;
}

/**
 * Rich attachment preview card — an image thumbnail, or a file card (type label + name). Replaces the
 * small attachment chips, both in the input bar (pre-send) and on the user message (post-send).
 */
export const AttachmentCardUI = ({ kind, src, alt, fileType, name, onClick, onRemove }: AttachmentCardUIProps) => {
  return (
    <div
      className={styles.card}
      data-kind={kind}
      data-clickable={onClick ? 'true' : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* decoding="async" on the image: canvas images arrive as multi-hundred-KB data URLs, and a
          synchronous decode of several of them lands on the main thread exactly while the chat rail is
          animating open. */}
      {kind === 'image' ? (
        <img className={styles.image} src={src} alt={alt ?? ''} loading="lazy" decoding="async" />
      ) : (
        <>
          <span className={styles.fileType}>{fileType}</span>
          <span className={styles.fileName}>{name}</span>
        </>
      )}

      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${name ?? alt ?? 'attachment'}`}
        >
          <Icon name="close-fill" size={14} />
        </button>
      )}
    </div>
  );
};
